import React, { createContext, useContext, useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Shared Modal component. Portals to document.body for correct stacking.
 *
 * Features:
 * - Portal rendering (escapes parent overflow:hidden)
 * - Flexbox centering (no transform hacks)
 * - Backdrop blur + click-to-close
 * - Escape key dismissal
 * - Body scroll lock
 * - CSS animation (no framer-motion dependency)
 * - Consistent z-index layering
 *
 * Usage:
 *   <Modal open={isOpen} onClose={() => setIsOpen(false)} width={520}>
 *     <Modal.Header title="My Modal" onClose={() => setIsOpen(false)} />
 *     <Modal.Body>content</Modal.Body>
 *   </Modal>
 */

// Z-index tiers for layered modals
const Z_TIERS = {
  default: 1000,
  overlay: 2000,   // image viewer, job detail
  critical: 5000,  // confirmations over other modals
};

const ModalContext = createContext(null);
const layers = [];
const isolated = new Map();
let originalOverflow, observer;
const ANNOUNCERS = '.ant-message, [data-modal-announcer]';
const topLayer = () => layers.reduce((top, item) => !top || item.zIndex >= top.zIndex ? item : top, null);

function restoreIsolation() {
  isolated.forEach((attributes, element) => {
    for (const [name, value] of Object.entries(attributes)) {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    }
  });
  isolated.clear();
}

// Isolate whole background branches, but leave noninteractive message portals
// available to screen readers. Recurse through their ancestors, not through
// the active modal. Lower modal layers are background until they become topmost.
function syncIsolation() {
  restoreIsolation();
  const top = topLayer();
  if (!top) return;
  if (!top.panel.contains(document.activeElement) || document.activeElement.matches(':disabled')) focusLayer(top);
  const visit = (element) => {
    if (element === top.backdrop || element.matches(ANNOUNCERS)) return;
    if (element.contains(top.backdrop) || element.querySelector(ANNOUNCERS)) {
      [...element.children].forEach(visit);
      return;
    }
    isolated.set(element, { 'aria-hidden': element.getAttribute('aria-hidden'), inert: element.getAttribute('inert') });
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('inert', '');
  };
  [...document.body.children].forEach(visit);
  layers.forEach(item => item.panel.setAttribute('aria-modal', String(item === top)));
}

function available(element) {
  if (!element?.isConnected || element.matches(':disabled') || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  for (let node = element; node && node !== document.body; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

function tabbable(panel) {
  return [...panel.querySelectorAll('button, a[href], input:not([type="hidden"]), select, textarea, [tabindex], [contenteditable="true"]')]
    .filter(element => element.tabIndex >= 0 && available(element))
    .sort((a, b) => (a.tabIndex || Infinity) - (b.tabIndex || Infinity));
}

function focusLayer(layer) {
  const requested = layer.options.current.initialFocusRef?.current;
  const heading = layer.panel.querySelector('[data-modal-heading]');
  const target = [requested, heading, ...tabbable(layer.panel)].find(element => layer.panel.contains(element) && available(element));
  (target || layer.panel).focus({ preventScroll: true });
}

function handleKeyDown(event) {
  const top = topLayer();
  if (!top || event.defaultPrevented) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    top.dismiss(true);
  } else if (event.key === 'Tab') {
    const items = tabbable(top.panel);
    const index = items.indexOf(document.activeElement);
    // Native traversal handles interior controls; explicitly wrap boundaries
    // and a heading/panel that is focused outside the tab sequence.
    if (!items.length || index === -1 || (!event.shiftKey && index === items.length - 1) || (event.shiftKey && index === 0)) {
      event.preventDefault();
      (items.length ? items[event.shiftKey ? items.length - 1 : 0] : top.panel).focus({ preventScroll: true });
    }
  }
}

function containFocus(event) {
  const top = topLayer();
  if (top && !top.panel.contains(event.target)) focusLayer(top);
}

function Modal({ open, onClose, children, width = 520, maxWidth = '92vw', maxHeight = '85vh', tier = 'default',
  closeDisabled = false, disableLightDismiss = false, initialFocusRef, returnFocusRef,
  className = '', backdropStyle, panelStyle, 'aria-label': ariaLabel, 'aria-labelledby': labelledBy,
  'aria-describedby': describedBy, 'aria-busy': busy,
}) {
  const backdropRef = useRef(null);
  const panelRef = useRef(null);
  const entry = useRef(null);
  const titleId = useId();
  const options = useRef(null);
  options.current = { onClose, closeDisabled, disableLightDismiss, initialFocusRef, returnFocusRef };
  const zIndex = backdropStyle?.zIndex ?? Z_TIERS[tier] ?? Z_TIERS.default;
  const dismiss = (light = false) => {
    const current = options.current;
    if (topLayer() === entry.current && !current.closeDisabled && !(light && current.disableLightDismiss)) current.onClose?.();
  };

  useLayoutEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    const layer = { backdrop: backdropRef.current, panel: panelRef.current, zIndex, options, dismiss, previousFocus };
    entry.current = layer;
    if (!layers.length) {
      originalOverflow = document.body.style.overflow;
      document.addEventListener('focusin', containFocus);
      window.addEventListener('keydown', handleKeyDown);
      observer = new MutationObserver(syncIsolation);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden'] });
    }
    layers.push(layer);
    document.body.style.overflow = 'hidden';
    // A newly inserted portal may have been isolated by an earlier layer.
    restoreIsolation();
    if (topLayer() === layer) focusLayer(layer);
    syncIsolation();
    return () => {
      const wasTop = topLayer() === layer;
      layers.splice(layers.indexOf(layer), 1);
      // Preserve the restoration chain when a parent closes before its child.
      layers.forEach(item => {
        if (layer.panel.contains(item.previousFocus)) item.previousFocus = layer.previousFocus;
      });
      entry.current = null;
      syncIsolation();
      const remaining = topLayer();
      if (!remaining) {
        observer.disconnect();
        document.removeEventListener('focusin', containFocus);
        window.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = originalOverflow;
      }
      if (wasTop) {
        const target = [layer.previousFocus, options.current.returnFocusRef?.current]
          .find(element => element !== document.body && available(element) && (!remaining || remaining.panel.contains(element)));
        if (target) target.focus({ preventScroll: true });
        else if (remaining) focusLayer(remaining);
      }
    };
    // Options are read through a ref so rerenders never reset focus/ownership.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, zIndex]);

  if (!open) return null;

  return createPortal(
    <ModalContext.Provider value={{ titleId, dismiss, closeDisabled }}><div
      ref={backdropRef}
      onClick={e => { if (e.target === backdropRef.current) dismiss(true); }}
      className={`uro-modal-backdrop ${className}`}
      style={{
        position: 'fixed', inset: 0, zIndex,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        animation: 'uroModalFadeIn 150ms ease',
        padding: 16, boxSizing: 'border-box', ...backdropStyle,
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={labelledBy || (ariaLabel ? undefined : titleId)}
        aria-describedby={describedBy}
        aria-busy={busy}
        tabIndex={-1}
        className="uro-modal-panel"
        style={{
          width, maxWidth, maxHeight,
          background: 'rgba(12,12,18,0.98)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 20,
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: 'uroModalSlideIn 200ms cubic-bezier(0.16, 1, 0.3, 1)', ...panelStyle,
        }}
      >
        <style>{`.uro-modal-panel :is(button, a, input, textarea, select, [tabindex]):focus-visible { outline: 2px solid #A5B4FC !important; outline-offset: 3px; } .uro-modal-panel:focus-visible { outline: 2px solid #A5B4FC; outline-offset: 3px; }`}</style>
        {children}
      </div>
    </div></ModalContext.Provider>,
    document.body
  );
}

function ModalHeader({ title, subtitle, onClose, children }) {
  const context = useContext(ModalContext);
  return (
    <div style={{
      padding: '16px 20px 12px',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div id={context?.titleId} data-modal-heading tabIndex={-1} style={{ fontSize: 16, fontWeight: 600, color: '#FFFFFF' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
      {onClose && (
        <button
          onClick={context ? () => context.dismiss() : onClose}
          disabled={context?.closeDisabled}
          aria-label="Close"
          style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: 'rgba(255,255,255,0.04)', border: 'none',
            color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontFamily: 'inherit',
            transition: 'background 150ms ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

function ModalBody({ children, style }) {
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', ...style }}>
      {children}
    </div>
  );
}

function ModalFooter({ children }) {
  return (
    <div style={{
      padding: '12px 20px 16px',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
    }}>
      {children}
    </div>
  );
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
